import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Info, X } from "@phosphor-icons/react";

const MODULES = [
  "dashboard", "lead", "follow_up", "deal", "quotation", "sales_order",
  "invoice", "collection", "payment_receipt", "customer", "product",
  "vendor", "purchase_order", "store", "delivery", "service",
  "installation", "amc", "hr", "reports", "whatsapp_templates",
  "whatsapp_logs", "admin_settings", "user_management",
];

const empty = () => ({
  module: "lead", field_key: "", title: "", purpose: "",
  who_can_use: "", steps: [], important: "", common_mistakes: "",
  is_active: true,
});

const AdminHelpContent = ({ user, logout }) => {
  const [items, setItems] = useState([]);
  const [moduleFilter, setModuleFilter] = useState("");
  const [editor, setEditor] = useState(null);
  const [stepsRaw, setStepsRaw] = useState("");

  const load = async () => {
    try {
      const params = new URLSearchParams({ include_inactive: "true" });
      if (moduleFilter) params.set("module", moduleFilter);
      const r = await axios.get(`${API}/help-content?${params.toString()}`);
      setItems(r.data || []);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [moduleFilter]);

  const openEditor = (entry) => {
    const e = entry ? { ...entry } : empty();
    setEditor(e);
    setStepsRaw((e.steps || []).join("\n"));
  };

  const save = async () => {
    if (!editor.module || !editor.title) { toast.error("Module + title required"); return; }
    const payload = { ...editor, steps: stepsRaw.split("\n").map(s => s.trim()).filter(Boolean) };
    try {
      const params = new URLSearchParams({ requester_role: user?.role || "" });
      if (editor.help_id) {
        await axios.put(`${API}/help-content/${editor.help_id}?${params.toString()}`, payload);
      } else {
        await axios.post(`${API}/help-content?${params.toString()}`, payload);
      }
      toast.success("Saved");
      setEditor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (entry) => {
    if (!window.confirm(`Delete "${entry.title}"?`)) return;
    try {
      await axios.delete(`${API}/help-content/${entry.help_id}?requester_role=${user?.role || ""}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64 p-8">
        <div className="flex justify-between items-start mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-1">Admin · Documentation</div>
            <h1 className="text-4xl sm:text-5xl tracking-tighter font-bold flex items-center gap-3" style={{fontFamily: 'Outfit, sans-serif'}}>
              <Info size={32} weight="bold" className="text-blue-700" />
              Help Content
            </h1>
            <p className="text-base text-slate-700 mt-1">{items.length} entries · edit module + field-level help shown across the app</p>
          </div>
          <button data-testid="help-new-btn" onClick={() => openEditor(null)} className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2.5 font-medium inline-flex items-center gap-2">
            <Plus size={16} weight="bold" /> New Entry
          </button>
        </div>

        <div className="bg-white border border-slate-200 p-4 mb-4">
          <select data-testid="help-filter-module" value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} className="rounded-none border border-slate-300 p-2 outline-none text-sm">
            <option value="">All modules</option>
            {MODULES.map(m => <option key={m}>{m}</option>)}
          </select>
        </div>

        <div data-testid="help-table" className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full">
            <thead><tr className="bg-slate-50 border-b border-slate-200">
              {["Module", "Field", "Title", "Purpose", "Status", "Actions"].map(h => (
                <th key={h} className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={6} className="p-12 text-center text-slate-400 text-sm">No help entries yet.</td></tr>
              ) : items.map((x, i) => (
                <tr key={x.help_id} data-testid={`help-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="p-3 text-xs uppercase tracking-wider"><span className="bg-slate-100 text-slate-700 px-2 py-0.5 border border-slate-200">{x.module}</span></td>
                  <td className="p-3 font-mono text-xs">{x.field_key || <span className="text-slate-400">—</span>}</td>
                  <td className="p-3 font-semibold">{x.title}</td>
                  <td className="p-3 text-xs text-slate-600 max-w-md truncate">{x.purpose}</td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${x.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                      {x.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button data-testid={`help-edit-${i}`} onClick={() => openEditor(x)} className="p-1.5 hover:bg-slate-100"><PencilSimple size={14} /></button>
                    {user?.role === "admin" && (
                      <button data-testid={`help-delete-${i}`} onClick={() => remove(x)} className="p-1.5 text-rose-700 hover:bg-rose-50 ml-1"><Trash size={14} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editor && (
        <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4" data-testid="help-editor">
          <div className="bg-white border border-slate-200 w-full max-w-2xl shadow-2xl max-h-[92vh] flex flex-col">
            <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2"><Info size={18} className="text-blue-700" weight="bold" /><h3 className="font-semibold">{editor.help_id ? "Edit" : "New"} Help Entry</h3></div>
              <button onClick={() => setEditor(null)} className="p-2 hover:bg-slate-100"><X size={16} /></button>
            </div>
            <div className="overflow-y-auto p-5 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Module *</label>
                  <select data-testid="help-fld-module" value={editor.module} onChange={e => setEditor({...editor, module: e.target.value})} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none">
                    {MODULES.map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Field key (optional)</label>
                  <input data-testid="help-fld-fieldkey" value={editor.field_key || ""} onChange={e => setEditor({...editor, field_key: e.target.value})} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none font-mono" placeholder="leave blank for module-level" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Status</label>
                  <select value={editor.is_active ? "1" : "0"} onChange={e => setEditor({...editor, is_active: e.target.value === "1"})} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none">
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Title *</label>
                <input data-testid="help-fld-title" value={editor.title} onChange={e => setEditor({...editor, title: e.target.value})} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Purpose</label>
                <textarea data-testid="help-fld-purpose" value={editor.purpose || ""} onChange={e => setEditor({...editor, purpose: e.target.value})} rows={2} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Who can use (module-level only)</label>
                <input value={editor.who_can_use || ""} onChange={e => setEditor({...editor, who_can_use: e.target.value})} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Steps · one per line</label>
                <textarea data-testid="help-fld-steps" value={stepsRaw} onChange={e => setStepsRaw(e.target.value)} rows={5} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none font-mono" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Important note</label>
                <input value={editor.important || ""} onChange={e => setEditor({...editor, important: e.target.value})} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Common mistakes</label>
                <input value={editor.common_mistakes || ""} onChange={e => setEditor({...editor, common_mistakes: e.target.value})} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none" />
              </div>
            </div>
            <div className="border-t border-slate-200 px-5 py-3 flex justify-end gap-2 bg-slate-50">
              <button onClick={() => setEditor(null)} className="rounded-none border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100">Cancel</button>
              <button data-testid="help-save" onClick={save} className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2 text-sm font-medium">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHelpContent;
